import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Session fermée depuis un autre appareil : on informe avant de renvoyer vers la connexion.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 401 && typeof detail === "string" && detail.includes("autre appareil")) {
      sessionStorage.setItem("kavizio_session_notice", detail);
      if (!window.location.pathname.startsWith("/connexion")) window.location.href = "/connexion";
    }
    return Promise.reject(err);
  },
);

const CURRENCY_FORMATS = {
  GNF: { suffix: " GNF", decimals: 0 },
  XOF: { suffix: " FCFA", decimals: 0 },
  EUR: { suffix: " €", decimals: 2 },
  USD: { suffix: " $", decimals: 2 },
};
let activeCurrency = "GNF";

export function setCurrency(code) {
  activeCurrency = CURRENCY_FORMATS[code] ? code : "GNF";
}

export function currentCurrency() {
  return activeCurrency;
}

export function fmtGNF(n) {
  const { suffix, decimals } = CURRENCY_FORMATS[activeCurrency];
  return new Intl.NumberFormat("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    .format(decimals ? n || 0 : Math.round(n || 0)) + suffix;
}

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export function apiError(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) {
    // Réponse non JSON (page d'erreur du serveur) : message lisible plutôt que du texte technique anglais.
    const status = e?.response?.status;
    if (status) return `Le serveur n'a pas pu traiter la demande (erreur ${status}). Réessayez dans un instant.`;
    return "Connexion au serveur impossible. Vérifiez votre réseau et réessayez.";
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (detail?.message) return detail.message;
  return detail?.msg || String(detail);
}

export function logError(...args) {
  if (process.env.NODE_ENV === "development") console.error(...args);
}

export async function openPdf(saleId, docType) {
  return openDocument(docType, saleId);
}

/** Ouvre un PDF dans un nouvel onglet via un lien signé (5 min) : fonctionne sur mobile et tablette,
 *  où le cookie de session n'accompagne pas toujours l'ouverture d'un nouvel onglet. */
export async function openDocument(kind, docId) {
  const tab = window.open("", "_blank");
  try {
    const { data } = await api.post("/documents/link", { kind, doc_id: docId });
    const url = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
    if (tab) tab.location.href = url;
    else window.location.assign(url);
    return url;
  } catch (e) {
    if (tab) tab.close();
    throw e;
  }
}

/** Partage natif du document (Web Share API) quand l'appareil le propose. */
export async function shareDocument(kind, docId, title) {
  const { data } = await api.post("/documents/link", { kind, doc_id: docId });
  const url = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
  if (navigator.share) {
    await navigator.share({ title, text: title, url });
    return true;
  }
  await navigator.clipboard.writeText(url);
  return false;
}

export function canShare() {
  return typeof navigator !== "undefined" && !!navigator.share;
}

export async function readSse(res, onEvent) {
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n\n");
    buf = lines.pop();
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const payload = line.slice(6);
      if (payload === "[DONE]") continue;
      try {
        onEvent(JSON.parse(payload));
      } catch (err) {
        logError("Fragment SSE partiel ignoré", err);
      }
    }
  }
}

export default api;
